import { useState, useEffect } from "react";
import { api } from "../api";

const STATUS_STYLE = {
  ok: "text-emerald-600 bg-emerald-50",
  error: "text-red-600 bg-red-50",
  skipped: "text-amber-600 bg-amber-50",
};

// Трейл попыток пуша в YClients (списание товара / оплата / комментарий+report_link) — см.
// logSync() в routes/documents.js. Единственное назначение — быстро ответить на «почему это не
// прокинулось в YClients», не гоняясь за SSH+SQL как раньше. Central-admin only.
export default function SyncLogPage() {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [locations, setLocations] = useState([]);
  const [location, setLocation] = useState("");
  const [status, setStatus] = useState("");
  const [issues, setIssues] = useState({ outbox: [], patients: [] });
  const [keys, setKeys] = useState([]);
  const [keyLocation, setKeyLocation] = useState("");
  const [createdKey, setCreatedKey] = useState("");
  const [actionError, setActionError] = useState("");

  const loadOperations = async () => {
    const [issueData, keyData] = await Promise.all([api.getSyncIssues(), api.listSyncKeys()]);
    setIssues(issueData);
    setKeys(keyData.items);
  };

  useEffect(() => {
    api.listLocations().then(setLocations).catch(() => {});
    Promise.all([api.getSyncIssues(), api.listSyncKeys()])
      .then(([issueData, keyData]) => { setIssues(issueData); setKeys(keyData.items); })
      .catch(err => setActionError(err.response?.data?.error || err.message));
  }, []);

  useEffect(() => {
    api.getSyncLog({ location: location || undefined, status: status || undefined, limit: 200 })
      .then(r => { setItems(r.items); setTotal(r.total); })
      .catch(() => setItems([]));
  }, [location, status]);

  const reconcile = async (id) => {
    setActionError("");
    try {
      await api.reconcileSyncIssue(id);
      await loadOperations();
    } catch (err) {
      setActionError(err.response?.data?.error || err.message);
    }
  };

  const createKey = async () => {
    if (!keyLocation) return;
    setActionError("");
    try {
      const result = await api.createSyncKey(keyLocation);
      setCreatedKey(result.key);
      await loadOperations();
    } catch (err) {
      setActionError(err.response?.data?.error || err.message);
    }
  };

  const revokeKey = async (id) => {
    if (!window.confirm("Отозвать этот ключ точки? Синхронизация по нему сразу прекратится.")) return;
    setActionError("");
    try {
      await api.revokeSyncKey(id);
      await loadOperations();
    } catch (err) {
      setActionError(err.response?.data?.error || err.message);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h2 className="admin-title">Лог синка с YClients {total ? `(${total})` : ""}</h2>
        <div className="flex items-center gap-2">
          <select className="h-8 text-xs rounded-md border border-slate-200 bg-white px-2" value={location} onChange={e => setLocation(e.target.value)}>
            <option value="">Все точки</option>
            {locations.map(l => <option key={l.id} value={l.name}>{l.name}</option>)}
          </select>
          <select className="h-8 text-xs rounded-md border border-slate-200 bg-white px-2" value={status} onChange={e => setStatus(e.target.value)}>
            <option value="">Любой статус</option>
            <option value="ok">ok</option>
            <option value="error">error</option>
            <option value="skipped">skipped</option>
          </select>
        </div>
      </div>

      {actionError && <div className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{actionError}</div>}

      <div className="grid gap-4 mb-5 lg:grid-cols-2">
        <section className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="font-semibold text-slate-800 mb-3">Требуют внимания</h3>
          {issues.outbox.length === 0 && issues.patients.length === 0 ? (
            <p className="text-sm text-slate-500">Нерешённых конфликтов и операций нет.</p>
          ) : (
            <div className="space-y-2 text-sm">
              {issues.outbox.map(item => (
                <div key={item.id} className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <div className="font-medium">YClients: {item.kind} · {item.status}</div>
                  <div className="text-xs text-slate-600 break-all">{item.resource || item.entityId}</div>
                  {item.lastError && <div className="text-xs text-red-700 mt-1">{item.lastError}</div>}
                  {item.status === "needs_review" && item.externalStarted === 1 && (
                    <button className="mt-2 text-xs font-medium text-blue-700" onClick={() => reconcile(item.id)}>
                      Проверить результат без повторной записи
                    </button>
                  )}
                </div>
              ))}
              {issues.patients.map(item => (
                <div key={item.id} className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <div className="font-medium">Пациент: {item.name}</div>
                  <div className="text-xs text-slate-600">{item.location || "Без точки"} · {item.conflict.type}</div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="font-semibold text-slate-800 mb-3">Ключи точек</h3>
          <div className="flex gap-2 mb-3">
            <select className="h-9 flex-1 rounded-md border border-slate-200 bg-white px-2 text-sm" value={keyLocation} onChange={e => setKeyLocation(e.target.value)}>
              <option value="">Выберите точку</option>
              {locations.map(item => <option key={item.id} value={item.name}>{item.name}</option>)}
            </select>
            <button className="rounded-md bg-blue-600 px-3 text-sm font-medium text-white disabled:opacity-50" disabled={!keyLocation} onClick={createKey}>Создать</button>
          </div>
          {createdKey && (
            <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
              <div className="text-xs font-medium text-blue-800">Скопируйте сейчас: повторно ключ не показывается.</div>
              <code className="mt-1 block break-all text-xs text-blue-900">{createdKey}</code>
              <button className="mt-2 text-xs font-medium text-blue-700" onClick={() => navigator.clipboard.writeText(createdKey)}>Скопировать</button>
            </div>
          )}
          <div className="space-y-2">
            {keys.map(item => (
              <div key={item.id} className="flex items-center justify-between gap-2 text-sm">
                <span>{item.location} <span className="text-xs text-slate-400">{item.revokedAt ? "отозван" : "активен"}</span></span>
                {!item.revokedAt && <button className="text-xs font-medium text-red-600" onClick={() => revokeKey(item.id)}>Отозвать</button>}
              </div>
            ))}
          </div>
        </section>
      </div>

      {items === null ? (
        <div className="docs-empty">Загрузка...</div>
      ) : items.length === 0 ? (
        <div className="docs-empty">Записей нет</div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr><th>Когда</th><th>Точка</th><th>Тип</th><th>Статус</th><th>Сообщение</th></tr>
          </thead>
          <tbody>
            {items.map(row => (
              <tr key={row.id}>
                <td className="whitespace-nowrap">{new Date(row.created_at).toLocaleString("ru-RU")}</td>
                <td>{row.location || "—"}</td>
                <td>{row.entity_type}</td>
                <td>
                  <span className={`text-[10px] font-semibold rounded-full px-1.5 py-0.5 ${STATUS_STYLE[row.status] || "text-slate-500 bg-slate-100"}`}>
                    {row.status}
                  </span>
                </td>
                <td className="text-slate-600">{row.message || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
